/**
 * PATH: packages/ui/src/components/settings/security-tab/MfaMethodsSection.tsx
 * FILE_ID: UI-SETTINGS-MFA-METHODS-001
 * PURPOSE: Enrol / switch / disable the SMS + Email second factors.
 * WHY: The authenticator-app (TOTP) flow lives in TwoFactorSection; this adds
 *      the text-message and email verification methods the backend exposes at
 *      /auth/mfa/* so users can pick whichever second factor suits them.
 * FLOW:
 *   ┌──────────┐   ┌──────────────────────┐   ┌────────────────────────┐
 *   │ status   │ → │ start (sms/email) +   │ → │ confirm code → primary │
 *   │ (GET)    │   │ deliver 6-digit code  │   │ method updated         │
 *   └──────────┘   └──────────────────────┘   └────────────────────────┘
 * DEPENDENCIES:
 *   - SettingsApiClient: injected HTTP bridge (baseURL already includes /api)
 *   - lucide-react: icons
 * RELATED:
 *   - SecurityTab.tsx (parent), TwoFactorSection.tsx (TOTP sibling).
 */

import { useCallback, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { Smartphone, Mail, Loader2, ShieldCheck, AlertTriangle } from 'lucide-react'
import type { SettingsApiClient } from '../types'

export interface MfaMethodsEndpoints {
  status: string
  smsStart: string
  smsConfirm: string
  emailStart: string
  emailConfirm: string
  disable: string
}

export interface MfaStatus {
  enabled: boolean
  method: string
  phone_masked?: string
  phone_verified?: boolean
  totp_enabled?: boolean
  available_methods?: string[]
}

export interface MfaMethodsSectionProps {
  apiClient: SettingsApiClient
  endpoints: MfaMethodsEndpoints
  /** Notify the parent so sibling sections (e.g. policy nag) can refresh. */
  onChanged?: () => void
}

type Mode = 'view' | 'sms-phone' | 'code' | 'disable'

interface MfaActionResponse {
  success?: boolean
  error?: string
  destination_masked?: string
}

export function MfaMethodsSection({ apiClient, endpoints, onChanged }: MfaMethodsSectionProps) {
  const [status, setStatus] = useState<MfaStatus | null>(null)
  const [mode, setMode] = useState<Mode>('view')
  const [enrolling, setEnrolling] = useState<'sms' | 'email' | null>(null)
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [password, setPassword] = useState('')
  const [destination, setDestination] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  const loadStatus = useCallback(async () => {
    try {
      setStatus(unwrap<MfaStatus>(await apiClient.get(endpoints.status)))
    } catch {
      setStatus(null)
    }
  }, [apiClient, endpoints.status])

  useEffect(() => { loadStatus() }, [loadStatus])

  const reset = useCallback(() => {
    setMode('view'); setEnrolling(null); setPhone(''); setCode('')
    setPassword(''); setDestination(''); setError(''); setBusy(false)
  }, [])

  const startSms = useCallback(async () => {
    setBusy(true); setError('')
    try {
      const data = unwrap<MfaActionResponse>(await apiClient.post(endpoints.smsStart, { phone }))
      if (data?.success) { setDestination(data.destination_masked || ''); setMode('code') }
      else setError(humaniseError(data?.error))
    } catch (e: unknown) { setError(errMessage(e, 'Could not send the code')) }
    finally { setBusy(false) }
  }, [apiClient, endpoints.smsStart, phone])

  const startEmail = useCallback(async () => {
    setEnrolling('email'); setBusy(true); setError('')
    try {
      const data = unwrap<MfaActionResponse>(await apiClient.post(endpoints.emailStart, {}))
      if (data?.success) { setDestination(data.destination_masked || ''); setMode('code') }
      else { setError(humaniseError(data?.error)); setEnrolling(null) }
    } catch (e: unknown) { setError(errMessage(e, 'Could not send the code')); setEnrolling(null) }
    finally { setBusy(false) }
  }, [apiClient, endpoints.emailStart])

  const confirm = useCallback(async () => {
    if (code.length !== 6 || !enrolling) return
    setBusy(true); setError('')
    const url = enrolling === 'sms' ? endpoints.smsConfirm : endpoints.emailConfirm
    try {
      const data = unwrap<MfaActionResponse>(await apiClient.post(url, { code }))
      if (data?.success) { await loadStatus(); onChanged?.(); reset() }
      else setError(humaniseError(data?.error) || 'That code was not valid')
    } catch (e: unknown) { setError(errMessage(e, 'Verification failed')) }
    finally { setBusy(false) }
  }, [apiClient, code, enrolling, endpoints.smsConfirm, endpoints.emailConfirm, loadStatus, onChanged, reset])

  const disable = useCallback(async () => {
    setBusy(true); setError('')
    try {
      const data = unwrap<MfaActionResponse>(await apiClient.post(endpoints.disable, { password }))
      if (data?.success) { await loadStatus(); onChanged?.(); reset() }
      else setError(humaniseError(data?.error) || 'Could not turn off verification')
    } catch (e: unknown) { setError(errMessage(e, 'Could not turn off verification')) }
    finally { setBusy(false) }
  }, [apiClient, endpoints.disable, password, loadStatus, onChanged, reset])

  const allowed = status?.available_methods ?? ['sms', 'email']
  const activeMethod = status?.method || ''
  const smsActive = activeMethod === 'sms'
  const emailActive = activeMethod === 'email'

  return (
    <section>
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 rounded-lg bg-primary/10">
          <Smartphone className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h3 className="font-semibold text-foreground">Text message &amp; email codes</h3>
          <p className="text-sm text-muted-foreground">
            Receive a one-time code by SMS or email when you sign in.
          </p>
        </div>
      </div>

      {/* Active method banner */}
      {(smsActive || emailActive) && mode === 'view' && (
        <div className="flex items-center justify-between p-4 rounded-xl bg-green-500/10 border border-green-500/20 mb-3">
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-5 h-5 text-green-500" />
            <div>
              <p className="font-medium text-green-600 dark:text-green-400">
                {smsActive ? 'Text message verification is on' : 'Email verification is on'}
              </p>
              {status?.phone_masked && smsActive && (
                <p className="text-sm text-muted-foreground">Codes sent to {status.phone_masked}</p>
              )}
            </div>
          </div>
          <button
            onClick={() => setMode('disable')}
            className="px-3 py-1.5 text-sm rounded-lg border border-destructive/50 text-destructive hover:bg-destructive/10"
          >
            Turn off
          </button>
        </div>
      )}

      {/* Method picker */}
      {mode === 'view' && (
        <div className="grid sm:grid-cols-2 gap-3">
          {allowed.includes('sms') && !smsActive && (
            <MethodCard
              icon={<Smartphone className="w-5 h-5 text-primary" />}
              title="Text message (SMS)"
              desc="Get a code by text to your mobile."
              cta="Set up SMS"
              onClick={() => { reset(); setEnrolling('sms'); setMode('sms-phone') }}
            />
          )}
          {allowed.includes('email') && !emailActive && (
            <MethodCard
              icon={<Mail className="w-5 h-5 text-primary" />}
              title="Email"
              desc="Get a code at your account email."
              cta="Set up email"
              onClick={startEmail}
              busy={busy && enrolling === 'email'}
            />
          )}
        </div>
      )}

      {/* SMS: phone entry */}
      {mode === 'sms-phone' && (
        <div className="bg-muted/30 rounded-xl p-6 border border-border space-y-3">
          <p className="text-sm font-medium">Enter your mobile number</p>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+61 4XX XXX XXX"
            className="w-full px-4 py-2 rounded-lg border border-border bg-background"
          />
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="flex gap-2">
            <button onClick={reset} className="px-4 py-2 rounded-lg border border-border hover:bg-muted">Cancel</button>
            <button
              onClick={startSms}
              disabled={busy || phone.trim().length < 6}
              className="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Send code
            </button>
          </div>
        </div>
      )}

      {/* Code confirmation (shared by sms + email) */}
      {mode === 'code' && (
        <div className="bg-muted/30 rounded-xl p-6 border border-border space-y-3">
          <p className="text-sm font-medium">Enter the 6-digit code</p>
          <p className="text-xs text-muted-foreground">
            We sent a code to {destination || (enrolling === 'sms' ? 'your phone' : 'your email')}.
          </p>
          <div className="flex gap-2">
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              className="flex-1 px-4 py-2 rounded-lg border border-border bg-background text-center font-mono text-lg tracking-widest"
              maxLength={6}
              autoFocus
            />
            <button
              onClick={confirm}
              disabled={busy || code.length !== 6}
              className="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 disabled:opacity-50"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify'}
            </button>
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <button onClick={reset} className="text-sm text-muted-foreground hover:text-foreground">Cancel</button>
        </div>
      )}

      {/* Disable confirmation (password) */}
      {mode === 'disable' && (
        <div className="bg-destructive/10 rounded-xl p-6 border border-destructive/20 space-y-3">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-destructive shrink-0" />
            <p className="text-sm text-muted-foreground">
              Turning off two-factor authentication makes your account less secure.
              Enter your password to confirm.
            </p>
          </div>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Account password"
            className="w-full px-4 py-2 rounded-lg border border-border bg-background"
          />
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="flex gap-2">
            <button onClick={reset} className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-muted">Cancel</button>
            <button
              onClick={disable}
              disabled={busy || !password}
              className="flex-1 px-4 py-2 rounded-lg bg-destructive text-destructive-foreground font-medium hover:bg-destructive/90 disabled:opacity-50"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Turn off'}
            </button>
          </div>
        </div>
      )}
    </section>
  )
}

function MethodCard({ icon, title, desc, cta, onClick, busy }: {
  icon: ReactNode; title: string; desc: string; cta: string
  onClick: () => void; busy?: boolean
}) {
  return (
    <div className="bg-muted/30 rounded-xl p-4 border border-border flex flex-col gap-2">
      <div className="flex items-center gap-2">{icon}<span className="font-medium">{title}</span></div>
      <p className="text-sm text-muted-foreground flex-1">{desc}</p>
      <button
        onClick={onClick}
        disabled={busy}
        className="self-start px-3 py-1.5 text-sm rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} {cta}
      </button>
    </div>
  )
}

/** Tolerate both raw bodies and axios-style `{ data }` envelopes. */
function unwrap<T>(response: unknown): T {
  if (response && typeof response === 'object' && 'data' in response) {
    return (response as { data: T }).data
  }
  return response as T
}

/** Pull a human message from an axios error / Error, else the fallback. */
function errMessage(e: unknown, fallback: string): string {
  if (e && typeof e === 'object') {
    const ax = e as { response?: { data?: { detail?: string } }; message?: string }
    if (ax.response?.data?.detail) return ax.response.data.detail
    if (ax.message) return ax.message
  }
  return fallback
}

function humaniseError(code?: string): string {
  switch (code) {
    case 'invalid_phone': return 'That phone number looks invalid. Use international format, e.g. +61…'
    case 'sms_unavailable': return 'SMS delivery is not configured. Try email instead.'
    case 'rate_limited': return 'Too many attempts. Please wait a minute and try again.'
    case 'email_failed': return 'We could not send the email. Try again shortly.'
    case 'verification_required': return 'Please confirm your password to continue.'
    default: return code || ''
  }
}

export default MfaMethodsSection
